/**
 * The type roles of `DESIGN.md` §3, as class sets rather than as a table nobody can apply.
 *
 * A component reaches for the role it needs (`type.pageTitle`) instead of re-deriving sizes and
 * weights, which is how five screens end up with five heading treatments. Every numeral role
 * carries `font-mono tabular-nums` — "what makes a column of scores line up, and its absence is
 * the difference between a designed table and a rendered one".
 */
export const type = {
  /**
   * Dashboard metrics (§6.2), and still only those.
   *
   * ⚠️ **This line read *"The only place a number is large."* until 2026-08-11, and the claim is
   * retracted rather than reworded** (TASK-118 decided it, TASK-119 made it false). §6.7's
   * per-question rating is a **margin mark** at `marginScore` below, so a large number now has two
   * homes — and the largest thing on that screen is not a number at all but a **word**, `verdict`.
   * `DESIGN.md` §3's *Typography* table carries both roles and says why neither is this one widened.
   */
  display: 'font-mono text-5xl font-medium tabular-nums tracking-tight text-ink-900',
  pageTitle: 'text-[22px] font-semibold tracking-tight text-ink-900',
  /** The signature device (§3 "The eyebrow"); `Eyebrow` composes it with its rule and count. */
  eyebrow: 'text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-500',
  sectionHeading: 'text-[15px] font-semibold text-ink-900',
  /** Table cells and body copy. The primary column of a table overrides to `text-ink-900`. */
  body: 'text-sm text-ink-700',
  bodyPrimary: 'text-sm text-ink-900',
  /** Timestamps, counts, labels. `ink-500` is the lightest text there is — never `ink-300`. */
  metadata: 'text-xs text-ink-500',
  /**
   * Free-text feedback: the one thing read carefully rather than scanned, so 15px, relaxed
   * leading and a 68ch measure. Never render a comment full-bleed across a wide monitor.
   */
  feedbackComment: 'text-[15px] leading-[1.7] text-ink-900 max-w-[68ch]',
  /**
   * §6.7's conclusion: **one interview's own overall recommendation**, at the size §6.2 gives a
   * dashboard metric, coloured by §3's *Statuses and recommendations* row and carrying **no dot** —
   * at this size the word is the shape, and a `size-1.5` dot beside it is a speck.
   *
   * ⚠️ **It is a word, so it is not in the mono face**: §3 gives `font-mono tabular-nums` to numerals
   * and to nothing else. And it is **§6.7's alone** — a verdict keyed on an *interviewer* rather than
   * on one interview is the column ADR 0015 exists to refuse, and rendering that large would be the
   * refusal broken loudly. `StatusDot` stays what §6.2, §6.3 and §6.5 render.
   */
  verdict: 'text-5xl font-medium leading-none tracking-tight',
  /**
   * §6.6 and §6.7's per-question rating, in the fixed-width left margin beside the wording it
   * belongs to. `ink-900` by default; `negative` at 1 and 2 (`toneForBucket`, which is §3's own
   * table read as meaning), and **no rule beneath** — at this size the numeral carries the colour.
   */
  marginScore: 'font-mono text-3xl font-medium tabular-nums leading-none',
  /**
   * §6.2's analysis band — a figure that is **context rather than a headline** (added 2026-08-11 with
   * TASK-129).
   *
   * ⚠️ **It exists so that `display` did not have to be narrowed.** §6.2 set its three health figures at
   * `display` and a real median of **0** then rendered at 48 px, which reads as a broken metric. The band
   * sits *below* the boundary that separates the leak from the analysis, so its figures are read after the
   * thing they contextualise; at 28 px they are still numerals a reader scans, and `display` keeps meaning
   * what §3's row says it means.
   */
  metricFigure: 'font-mono text-[28px] font-medium tabular-nums leading-none text-ink-900',
  /** Any numeral outside the display role: scores, counts, dates, averages. Without exception. */
  numeral: 'font-mono tabular-nums',
} as const;

export type TypeRole = keyof typeof type;
